import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, Check, CheckCheck, Trash2, X, Car, Star, 
  MapPin, DollarSign, Clock, Route, AlertCircle, 
  Eye, UserPlus, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { notificationAPI, adminAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { devError } from '../lib/devLog';

const NOTIFICATION_ICONS = {
  job_confirmed: Car,
  job_accepted: Check,
  driver_en_route: MapPin,
  job_completed: CheckCheck,
  payment_receipt: DollarSign,
  job_assigned: Car,
  job_cancelled: X,
  new_job_available: AlertCircle,
  new_job: UserPlus, // Used for new driver applications
  route_booked: Route,
  booking_cancelled: X,
  rating_received: Star,
};

const NOTIFICATION_COLORS = {
  job_confirmed: 'bg-blue-100 text-blue-600',
  job_accepted: 'bg-green-100 text-green-600',
  driver_en_route: 'bg-purple-100 text-purple-600',
  job_completed: 'bg-green-100 text-green-600',
  payment_receipt: 'bg-emerald-100 text-emerald-600',
  job_assigned: 'bg-blue-100 text-blue-600',
  job_cancelled: 'bg-red-100 text-red-600',
  new_job_available: 'bg-orange-100 text-orange-600',
  new_job: 'bg-yellow-100 text-yellow-600', // Used for new driver applications
  route_booked: 'bg-indigo-100 text-indigo-600',
  booking_cancelled: 'bg-red-100 text-red-600',
  rating_received: 'bg-yellow-100 text-yellow-600',
};

// Format vehicle type for display
const formatVehicleType = (type) => {
  if (!type) return 'N/A';
  const formats = {
    'car': 'Car',
    'motorcycle': 'Motorcycle',
    'ebike': 'E-Bike',
    'bicycle': 'Bicycle',
    'van': 'Van',
    'pickup_truck': 'Pickup Truck',
    'box_truck': 'Box Truck',
    'flatbed': 'Flatbed'
  };
  return formats[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  
  // Detail dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [driverDetail, setDriverDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Fetch notifications
  const fetchNotifications = async () => {
    try {
      const [notifRes, countRes] = await Promise.all([
        notificationAPI.getAll(false, 20),
        notificationAPI.getUnreadCount(),
      ]);
      setNotifications(notifRes.data);
      setUnreadCount(countRes.data.unread_count);
    } catch (error) {
      devError('Failed to fetch notifications:', error);
    }
  };

  // Initial fetch and polling
  useEffect(() => {
    fetchNotifications();
    
    // Poll for new notifications every 10 seconds
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationAPI.markRead([notificationId]);
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      devError('Failed to mark as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    setLoading(true);
    try {
      await notificationAPI.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      devError('Failed to mark all as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await notificationAPI.delete(notificationId);
      const notification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (notification && !notification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      devError('Failed to delete notification:', error);
    }
  };

  // Handle clicking on a notification to view details
  const handleNotificationClick = async (notification) => {
    // Mark as read first
    if (!notification.is_read) {
      await handleMarkAsRead(notification.id);
    }
    
    setSelectedNotification(notification);
    setDriverDetail(null);
    
    // Check if this is a driver application notification (has driver_id in data)
    const isDriverApplication = notification.title?.includes('Driver Application') && 
                                notification.data?.driver_id;
    
    if (isDriverApplication && user?.role === 'admin') {
      setLoadingDetail(true);
      try {
        const res = await adminAPI.getDriverDetails(notification.data.driver_id);
        setDriverDetail(res.data);
      } catch (error) {
        devError('Failed to fetch driver details:', error);
        // Driver might have been deleted or already processed
        setDriverDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    }
    
    setShowDetailDialog(true);
    setIsOpen(false);
  };

  // Navigate to admin approvals tab
  const handleGoToApprovals = () => {
    setShowDetailDialog(false);
    navigate('/admin?tab=approvals');
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getIcon = (type) => {
    return NOTIFICATION_ICONS[type] || Bell;
  };

  const getColor = (type) => {
    return NOTIFICATION_COLORS[type] || 'bg-zinc-100 text-zinc-600';
  };

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        {/* Bell Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative p-2 rounded-full hover:bg-zinc-100 transition-colors"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5 text-zinc-600" />
          {unreadCount > 0 && (
            <span 
              className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center"
              data-testid="notification-badge"
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border border-zinc-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="font-semibold text-zinc-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Notifications
            </h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                disabled={loading}
                className="text-xs text-orange-600 hover:text-orange-700"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-4 h-4 mr-1" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-10 h-10 text-zinc-300 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const Icon = getIcon(notification.type);
                const colorClass = getColor(notification.type);
                const hasDetails = notification.data?.driver_id || notification.data?.job_id;
                
                return (
                  <div
                    key={notification.id}
                    className={`px-4 py-3 border-b border-zinc-50 hover:bg-zinc-50 transition-colors cursor-pointer ${
                      !notification.is_read ? 'bg-orange-50/50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                    data-testid={`notification-${notification.id}`}
                  >
                    <div className="flex gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${!notification.is_read ? 'font-medium text-zinc-900' : 'text-zinc-700'}`}>
                            {notification.title}
                          </p>
                          <span className="text-xs text-zinc-400 flex-shrink-0">
                            {formatTime(notification.created_at)}
                          </span>
                        </div>
                        <p className="text-sm text-zinc-500 mt-0.5 line-clamp-2">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          {hasDetails && (
                            <span className="text-xs text-orange-600 flex items-center gap-1">
                              <Eye className="w-3 h-3" />
                              View details
                            </span>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(notification.id);
                            }}
                            className="text-xs text-zinc-400 hover:text-red-500 flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete
                          </button>
                        </div>
                      </div>
                      {!notification.is_read && (
                        <div className="w-2 h-2 bg-orange-500 rounded-full flex-shrink-0 mt-2"></div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 border-t border-zinc-100 bg-zinc-50">
              <p className="text-xs text-zinc-400 text-center">
                Showing {notifications.length} most recent notifications
              </p>
            </div>
          )}
        </div>
      )}
    </div>

    {/* Notification Detail Dialog - Outside dropdown for proper portal rendering */}
    <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }} className="flex items-center gap-2">
            {selectedNotification && (() => {
              const Icon = getIcon(selectedNotification.type);
              const colorClass = getColor(selectedNotification.type);
              return (
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${colorClass}`}>
                  <Icon className="w-4 h-4" />
                </div>
              );
            })()}
            {selectedNotification?.title}
          </DialogTitle>
          <DialogDescription>
            {selectedNotification && formatTime(selectedNotification.created_at)}
          </DialogDescription>
        </DialogHeader>
        
        {selectedNotification && (
          <div className="space-y-4">
            {/* Message */}
            <div className="bg-zinc-50 rounded-lg p-4">
              <p className="text-zinc-700">{selectedNotification.message}</p>
            </div>

            {/* Driver Application Details (for admin) */}
            {loadingDetail && (
              <div className="text-center py-4">
                <div className="animate-spin w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full mx-auto" />
                <p className="text-sm text-zinc-500 mt-2">Loading details...</p>
              </div>
            )}

            {driverDetail && user?.role === 'admin' && (
              <div className="space-y-3">
                <h4 className="font-semibold text-zinc-900 flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-orange-500" />
                  Driver Application Details
                </h4>
                
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-2">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-zinc-500">Name:</span>
                      <p className="font-medium text-zinc-900">{driverDetail.name}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Email:</span>
                      <p className="font-medium text-zinc-900">{driverDetail.email}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Phone:</span>
                      <p className="font-medium text-zinc-900">{driverDetail.phone}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Vehicle:</span>
                      <p className="font-medium text-zinc-900">{formatVehicleType(driverDetail.vehicle_type)}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">License #:</span>
                      <p className="font-medium text-zinc-900 font-mono">{driverDetail.drivers_license || 'N/A'}</p>
                    </div>
                    <div>
                      <span className="text-zinc-500">Status:</span>
                      <p className={`font-medium capitalize ${
                        driverDetail.driver_status === 'approved' ? 'text-green-600' :
                        driverDetail.driver_status === 'rejected' ? 'text-red-600' :
                        'text-yellow-600'
                      }`}>
                        {driverDetail.driver_status || 'pending'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Action Button */}
                {driverDetail.driver_status === 'pending' && (
                  <Button
                    onClick={handleGoToApprovals}
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="go-to-approvals-btn"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Review in Approvals Dashboard
                  </Button>
                )}
              </div>
            )}

            {/* No driver details found */}
            {selectedNotification.data?.driver_id && !loadingDetail && !driverDetail && user?.role === 'admin' && (
              <div className="bg-zinc-100 rounded-lg p-4 text-center">
                <p className="text-sm text-zinc-600">
                  Driver details not available. The application may have already been processed.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGoToApprovals}
                  className="mt-2"
                >
                  View All Approvals
                </Button>
              </div>
            )}

            {/* Close Button */}
            <Button
              variant="outline"
              onClick={() => setShowDetailDialog(false)}
              className="w-full"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  </>
  );
};

export default NotificationBell;
